/**
 * Risk-band distribution panel — policy §5 pattern-signals tile.
 *
 * Reads from `GET /api/v1/insights/pattern-signals` and renders a small
 * stacked-bar of HIGH / MEDIUM / LOW counts with a plain-language caption.
 * No × button — the panel self-clears when the signal goes away
 * (`totalEmployees === 0`).
 *
 * Shown on:
 *   - `/manager` (Manager + HOD acting as manager)
 *   - `/hod/overview`
 *   - `/hr/insights`
 *
 * NOT shown on:
 *   - `/director` (policy §5 — too low-level for Director)
 */
import { useQuery } from '@tanstack/react-query'
import { Card } from '../primitives/Card'
import { PATTERN_EXPLAINERS } from '../../lib/pattern-catalog-schemas'
import { getPatternSignals } from '../../api/team-insights'

const STALE_TIME_MS = 60_000

function humanise(code: string): string {
  return code
    .split('_')
    .filter(Boolean)
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')
}

export function RiskBandPanel(): JSX.Element | null {
  const query = useQuery({
    queryKey: ['insights', 'pattern-signals'],
    queryFn: getPatternSignals,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const data = query.data
  if (!data) return null
  if (data.totalEmployees === 0) return null

  const total = Math.max(1, data.high + data.medium + data.low)
  const highPct = (data.high / total) * 100
  const mediumPct = (data.medium / total) * 100
  const lowPct = (data.low / total) * 100

  const explainer = data.mostCommonPattern
    ? PATTERN_EXPLAINERS[data.mostCommonPattern]
    : null

  return (
    <Card padding="14px 18px" data-testid="risk-band-panel">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 10,
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          Pattern signals
        </h2>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          {data.totalEmployees} {data.totalEmployees === 1 ? 'person' : 'people'} {data.scopeLabel}
        </span>
      </div>

      {/* Stacked bar */}
      <div
        role="img"
        aria-label={`${data.high} high, ${data.medium} medium, ${data.low} low`}
        style={{
          display: 'flex',
          height: 12,
          borderRadius: 999,
          overflow: 'hidden',
          background: 'var(--surface-2)',
          border: '1px solid var(--line-subtle)',
        }}
      >
        {data.high > 0 ? (
          <div
            data-band="high"
            style={{ width: `${highPct}%`, background: 'var(--danger)' }}
            title={`${data.high} high`}
          />
        ) : null}
        {data.medium > 0 ? (
          <div
            data-band="medium"
            style={{ width: `${mediumPct}%`, background: 'var(--warning)' }}
            title={`${data.medium} medium`}
          />
        ) : null}
        {data.low > 0 ? (
          <div
            data-band="low"
            style={{ width: `${lowPct}%`, background: 'var(--success)' }}
            title={`${data.low} low`}
          />
        ) : null}
      </div>

      {/* Counts */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginTop: 10,
          fontSize: 12,
          color: 'var(--text-2)',
        }}
      >
        <BandLegend label="High" value={data.high} swatch="var(--danger)" />
        <BandLegend label="Medium" value={data.medium} swatch="var(--warning)" />
        <BandLegend label="Low" value={data.low} swatch="var(--success)" />
      </div>

      {/* Caption */}
      {data.mostCommonPattern ? (
        <div
          style={{
            marginTop: 10,
            paddingTop: 8,
            borderTop: '1px solid var(--line-subtle)',
            fontSize: 12,
            color: 'var(--text-3)',
            lineHeight: 1.5,
          }}
        >
          Most common signal: <strong style={{ color: 'var(--text-2)' }}>{humanise(data.mostCommonPattern)}</strong>
          {data.mostCommonPatternCount > 0
            ? ` (${data.mostCommonPatternCount} instance${data.mostCommonPatternCount === 1 ? '' : 's'})`
            : null}
          {explainer ? ` — ${explainer.detects}` : null}
        </div>
      ) : null}
    </Card>
  )
}

interface BandLegendProps {
  label: string
  value: number
  swatch: string
}

function BandLegend({ label, value, swatch }: BandLegendProps): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        aria-hidden="true"
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: swatch,
          display: 'inline-block',
        }}
      />
      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{value}</span>
      <span style={{ color: 'var(--text-3)' }}>{label}</span>
    </span>
  )
}
